import { CommonNodeViewWrapper } from '@/components/common/node-view-wrapper';
import { useIsEditorReadonly } from '@/hooks/use-is-editor-readonly';
import { useNodeViewCollaborationPresence } from '@/hooks/use-node-view-collaboration-presence';
import type { OnMount } from '@monaco-editor/react';
import { Editor as MonacoEditor } from '@monaco-editor/react';
import type { ReactNodeViewProps } from '@tiptap/react';
import { NodeViewContent } from '@tiptap/react';
import { ChevronRight, Plus, Trash2 } from 'lucide-react';
import { memo, useCallback, useMemo, useState } from 'react';
import { setupMonacoEditorOnMount } from '../monaco-editor-utils';
import { type ZyDEProgramFile, defaultExtension, defaultMainFilename, ZYDE_LANGUAGE_OPTIONS } from './zyde-extension';

function suggestedNewFilename(language: string, existing: string[]): string {
    const ext = defaultExtension(language);
    for (let n = 2; n < 50; n++) {
        const name = `file${n}.${ext}`;
        if (!existing.map(e => e.trim().toLowerCase()).includes(name.toLowerCase())) {
            return name;
        }
    }
    return `file_${Date.now()}.${ext}`;
}

function getEffectiveFiles(multifiles: ZyDEProgramFile[]): ZyDEProgramFile[] {
    if (multifiles.length > 0) {
        return ensureOneMain(
            multifiles.map(f => ({
                program: f.program ?? '',
                filename: f.filename ?? '',
                main: Boolean(f.main),
                data: Boolean(f.data),
            })),
        );
    }
    return [{ program: '', filename: '', main: true, data: false }];
}

function ensureOneMain(files: ZyDEProgramFile[]): ZyDEProgramFile[] {
    const idx = files.findIndex(f => f.main);
    if (idx === -1) {
        return files.map((f, i) => ({ ...f, main: i === 0 }));
    }
    return files.map((f, i) => ({ ...f, main: i === idx }));
}

function packFiles(files: ZyDEProgramFile[]): { multifiles: ZyDEProgramFile[] } {
    const normalized = ensureOneMain(
        files.map(f => ({
            program: f.program ?? '',
            filename: (f.filename ?? '').trim(),
            main: Boolean(f.main),
            data: Boolean(f.data),
        })),
    );
    return { multifiles: normalized };
}

const ZyDENodeView: React.FC<ReactNodeViewProps> = ({ node, updateAttributes, selected, editor, decorations }) => {
    const isEditorReadonly = useIsEditorReadonly(editor);
    const { id } = node.attrs;
    const language = node.attrs.language || 'cpp';
    const caption = (node.attrs.caption as string | null) ?? '';
    const inputEnabled = Boolean(node.attrs.inputEnabled);
    const resetEnabled = Boolean(node.attrs.resetEnabled);
    const stacked = Boolean(node.attrs.stacked);
    const activityType = node.attrs.activityType as string | null;
    const inputText = (node.attrs.inputText as string) || '';
    const outputFiles = (node.attrs.outputFiles as string) || '';
    const multifiles = useMemo(() => (node.attrs.multifiles as ZyDEProgramFile[]) || [], [node.attrs.multifiles]);
    const files = useMemo(() => getEffectiveFiles(multifiles), [multifiles]);

    const [activeIndex, setActiveIndex] = useState(0);
    const [showAdvanced, setShowAdvanced] = useState(false);
    const [isMonacoEditorFocused, setIsMonacoEditorFocused] = useState(false);
    const { presenceUsers, presenceTracking } = useNodeViewCollaborationPresence(editor, id, 'zyDE', isMonacoEditorFocused);

    const safeActiveIndex = Math.min(activeIndex, Math.max(0, files.length - 1));
    const activeFile = files[safeActiveIndex];
    const editorValue = activeFile?.program ?? '';

    const commitFiles = useCallback(
        (nextFiles: ZyDEProgramFile[]) => {
            updateAttributes(packFiles(nextFiles));
        },
        [updateAttributes],
    );

    const handleProgramChange = useCallback(
        (value: string | undefined) => {
            if (value === undefined) {
                return;
            }
            const next = files.map((f, j) => (j === safeActiveIndex ? { ...f, program: value } : f));
            commitFiles(next);
        },
        [safeActiveIndex, files, commitFiles],
    );

    const setMainFile = useCallback(
        (index: number) => {
            const next = files.map((f, j) => ({ ...f, main: j === index }));
            commitFiles(next);
        },
        [files, commitFiles],
    );

    const updateFilename = useCallback(
        (index: number, filename: string) => {
            const next = files.map((f, j) => (j === index ? { ...f, filename } : f));
            commitFiles(next);
        },
        [files, commitFiles],
    );

    const toggleDataFile = useCallback(
        (index: number) => {
            const next = files.map((f, j) => (j === index ? { ...f, data: !f.data } : f));
            commitFiles(next);
        },
        [files, commitFiles],
    );

    const addFile = useCallback(() => {
        const existingNames = files.map(f => f.filename);
        const newFile: ZyDEProgramFile = {
            program: '',
            filename: suggestedNewFilename(language, existingNames),
            main: false,
            data: false,
        };
        let next: ZyDEProgramFile[];
        if (multifiles.length === 0 && files.length === 1) {
            const first = {
                ...files[0],
                filename: files[0].filename || defaultMainFilename(language),
                main: true,
            };
            next = [...ensureOneMain([first]), newFile];
        } else {
            next = [...files, newFile];
        }
        commitFiles(ensureOneMain(next));
        setActiveIndex(next.length - 1);
    }, [files, multifiles.length, language, commitFiles]);

    const removeFile = useCallback(
        (index: number) => {
            if (files.length <= 1) {
                return;
            }
            if (!window.confirm('Are you sure you want to delete this file?')) {
                return;
            }
            const next = files.filter((_, j) => j !== index);
            commitFiles(ensureOneMain(next));
            setActiveIndex(i => Math.min(i, next.length - 1));
        },
        [files, commitFiles],
    );

    const getMonacoLanguage = (lang: string): string => {
        const map: Record<string, string> = {
            cpp: 'cpp',
            c: 'c',
            java: 'java',
            python: 'python',
            python3: 'python',
            r: 'r',
            sql: 'sql',
            verilog: 'verilog',
            web: 'html',
        };
        return map[lang] || 'plaintext';
    };

    const handleMonacoMount: OnMount = instance => {
        setupMonacoEditorOnMount(
            instance,
            () => setIsMonacoEditorFocused(true),
            () => setIsMonacoEditorFocused(false),
        );
    };

    const mainGroupName = `zyde-main-${node.attrs.id || 'node'}`;

    const settingsPanel = (
        <div
            contentEditable={false}
            className="rounded border-b border-gray-200 bg-gray-50 px-4 pt-6 pb-3 dark:border-gray-700 dark:bg-gray-800"
        >
            <fieldset
                disabled={isEditorReadonly}
                className="m-0 min-w-0 space-y-3 border-0 p-0"
            >
                <div className="grid grid-cols-2 gap-4">
                    <div>
                        <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400">Language</label>
                        <select
                            value={language}
                            onChange={e => updateAttributes({ language: e.target.value || 'cpp' })}
                            className="w-full rounded border border-gray-300 bg-white px-3 py-1 text-sm text-gray-800 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 focus:outline-none dark:border-gray-600 dark:bg-gray-700 dark:text-gray-200"
                            data-search-attr="language"
                        >
                            {ZYDE_LANGUAGE_OPTIONS.map(opt => (
                                <option
                                    key={opt.value}
                                    value={opt.value}
                                >
                                    {opt.label}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400">Activity type</label>
                        <select
                            value={activityType || 'participation'}
                            onChange={e =>
                                updateAttributes({
                                    activityType: e.target.value || null,
                                })
                            }
                            className="w-full rounded border border-gray-300 bg-white px-3 py-1 text-sm text-gray-800 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 focus:outline-none dark:border-gray-600 dark:bg-gray-700 dark:text-gray-200"
                            data-search-attr="activityType"
                        >
                            <option value="participation">Participation</option>
                            <option value="tinker">Tinker</option>
                        </select>
                    </div>
                </div>

                <div>
                    <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400">Caption</label>
                    <input
                        type="text"
                        value={caption}
                        onChange={e =>
                            updateAttributes({
                                caption: e.target.value.trim() === '' ? null : e.target.value,
                            })
                        }
                        placeholder="Optional caption"
                        data-search-attr="caption"
                        className="w-full rounded border border-gray-300 bg-white px-3 py-1 text-sm text-gray-800 placeholder:text-gray-400 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 focus:outline-none dark:border-gray-600 dark:bg-gray-700 dark:text-gray-200 dark:placeholder:text-gray-500"
                    />
                </div>

                <div className="border-gray-200 pt-2 dark:border-gray-700">
                    <button
                        type="button"
                        onClick={() => setShowAdvanced(!showAdvanced)}
                        className="flex items-center gap-2 text-sm font-medium text-gray-700 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200"
                        contentEditable={false}
                    >
                        <ChevronRight className={`h-4 w-4 transition-transform ${showAdvanced ? 'rotate-90' : ''}`} />
                        Advanced Settings
                    </button>

                    {showAdvanced && (
                        <div className="mt-3 space-y-3 rounded border border-gray-200 bg-gray-50 p-3 dark:border-gray-700 dark:bg-gray-900/50">
                            <div className="flex flex-wrap items-center gap-6">
                                <label className="flex items-center gap-2">
                                    <input
                                        type="checkbox"
                                        checked={inputEnabled}
                                        onChange={e => updateAttributes({ inputEnabled: e.target.checked })}
                                        className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700"
                                    />
                                    <span className="text-xs text-gray-700 dark:text-gray-300">Input enabled</span>
                                </label>
                                <label className="flex items-center gap-2">
                                    <input
                                        type="checkbox"
                                        checked={resetEnabled}
                                        onChange={e => updateAttributes({ resetEnabled: e.target.checked })}
                                        className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700"
                                    />
                                    <span className="text-xs text-gray-700 dark:text-gray-300">Reset enabled</span>
                                </label>
                                <label className="flex items-center gap-2">
                                    <input
                                        type="checkbox"
                                        checked={stacked}
                                        onChange={e => updateAttributes({ stacked: e.target.checked })}
                                        className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700"
                                    />
                                    <span className="text-xs text-gray-700 dark:text-gray-300">Stacked</span>
                                </label>
                            </div>
                            <div>
                                <label className="mb-1 block text-xs font-medium text-gray-700 dark:text-gray-300">
                                    Stdin template (zyInput)
                                </label>
                                <textarea
                                    value={inputText}
                                    onChange={e => updateAttributes({ inputText: e.target.value })}
                                    rows={3}
                                    className="w-full rounded border border-gray-300 bg-white px-3 py-2 font-mono text-xs text-gray-800 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-200"
                                />
                            </div>
                            <div>
                                <label className="mb-1 block text-xs font-medium text-gray-700 dark:text-gray-300">Output files</label>
                                <input
                                    type="text"
                                    value={outputFiles}
                                    onChange={e => updateAttributes({ outputFiles: e.target.value })}
                                    className="w-full rounded border border-gray-300 bg-white px-3 py-1 text-sm text-gray-800 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-200"
                                />
                            </div>
                        </div>
                    )}
                </div>
            </fieldset>
        </div>
    );

    const fileToolbar = (
        <div
            contentEditable={false}
            className="flex flex-wrap items-center gap-1 border-b border-gray-200 bg-gray-100 px-2 py-1 dark:border-gray-600 dark:bg-gray-900"
        >
            {files.map((f, i) => (
                <button
                    key={i}
                    type="button"
                    onClick={() => setActiveIndex(i)}
                    className={`flex max-w-40 items-center gap-1 truncate rounded px-2 py-1 text-xs font-medium ${
                        i === safeActiveIndex
                            ? 'bg-white text-gray-900 shadow-sm dark:bg-gray-800 dark:text-gray-100'
                            : 'text-gray-600 hover:bg-gray-200 dark:text-gray-400 dark:hover:bg-gray-800'
                    }`}
                    title={f.filename || `File ${i + 1}`}
                >
                    <span className="truncate">{f.filename || `File ${i + 1}`}</span>
                    {f.main && (
                        <span
                            className="shrink-0 rounded bg-blue-600 px-1 text-[10px] font-semibold text-white"
                            title="Main entry file"
                        >
                            main
                        </span>
                    )}
                </button>
            ))}
            <button
                type="button"
                onClick={addFile}
                disabled={isEditorReadonly}
                className="ml-1 flex items-center gap-0.5 rounded px-2 py-1 text-xs font-medium text-blue-700 hover:bg-blue-50 disabled:cursor-not-allowed disabled:opacity-50 dark:text-blue-400 dark:hover:bg-gray-800"
                title="Add another source file"
            >
                <Plus className="size-3.5" />
                Add file
            </button>
        </div>
    );

    const activeFilePanel = activeFile ? (
        <fieldset
            disabled={isEditorReadonly}
            className="m-0 min-w-0 space-y-2 border-0 border-b border-gray-200 bg-gray-50 px-3 py-2 dark:border-gray-600 dark:bg-gray-800/80"
        >
            <div className="flex flex-wrap items-center gap-3">
                <div className="min-w-48 flex-1">
                    <label className="mb-0.5 block text-[10px] font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
                        Filename
                    </label>
                    <input
                        type="text"
                        value={activeFile.filename}
                        onChange={e => updateFilename(safeActiveIndex, e.target.value)}
                        className="w-full rounded border border-gray-300 bg-white px-2 py-1 font-mono text-xs text-gray-800 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-200"
                    />
                </div>
                <label className="flex cursor-pointer items-center gap-2 pt-5">
                    <input
                        type="radio"
                        name={mainGroupName}
                        checked={activeFile.main}
                        onChange={() => setMainFile(safeActiveIndex)}
                        className="h-4 w-4 border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-xs text-gray-700 dark:text-gray-300">Main file</span>
                </label>
                <label className="flex cursor-pointer items-center gap-2 pt-5">
                    <input
                        type="checkbox"
                        checked={activeFile.data}
                        onChange={() => toggleDataFile(safeActiveIndex)}
                        className="h-4 w-4 rounded border-gray-300 text-blue-600 dark:border-gray-600"
                    />
                    <span className="text-xs text-gray-700 dark:text-gray-300">Do not compile</span>
                </label>
                <div className="ml-auto flex items-center gap-1 pt-5">
                    {files.length > 1 && (
                        <button
                            type="button"
                            onClick={() => removeFile(safeActiveIndex)}
                            className="flex items-center gap-1 rounded px-2 py-1 text-xs text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-gray-800"
                            title="Remove this file"
                        >
                            <Trash2 className="size-3.5" />
                        </button>
                    )}
                </div>
            </div>
        </fieldset>
    ) : null;

    const mainContent = (
        <>
            <NodeViewContent />
            <div className="rounded border border-gray-300 bg-white mx-4 mb-2 h-full">
                {fileToolbar}
                {activeFilePanel}
                <div
                    className="flex-1 h-48"
                    contentEditable={false}
                >
                    <MonacoEditor
                        key={`zyde-${safeActiveIndex}-${files.length}`}
                        language={getMonacoLanguage(language)}
                        value={editorValue}
                        onChange={handleProgramChange}
                        options={{
                            minimap: { enabled: false },
                            lineNumbers: 'on',
                            scrollBeyondLastLine: false,
                            automaticLayout: true,
                            tabSize: 2,
                            wordWrap: 'on',
                            readOnly: isEditorReadonly,
                        }}
                        onMount={handleMonacoMount}
                    />
                </div>
            </div>
        </>
    );

    return (
        <CommonNodeViewWrapper
            decorations={decorations}
            label="zyDE"
            selected={selected}
            settingsPanel={settingsPanel}
            contentClassName="px-0 py-0"
            presenceTracking={presenceTracking}
            presenceUsers={presenceUsers}
            fullscreenContent={mainContent}
            fullscreenMode="inline"
            contentHeightClassName=""
            containerClassName="bg-white dark:bg-black"
        >
            {mainContent}
        </CommonNodeViewWrapper>
    );
};

export default memo(ZyDENodeView);
